import React, { useEffect, useMemo, useState } from 'react';
import { MAPS_API_KEY, USE_OSM_ONLY } from '../config/maps';

type LatLng = { lat: number; lng: number };

export type AutocompleteSuggestion = {
  id: string;
  primaryText: string;
  secondaryText?: string;
  lat?: number;
  lon?: number;
};

export type PlaceSelection = {
  name?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  coordinates?: LatLng;
};

interface PlaceAutocompleteProps {
  value?: string;
  onSelect: (sel: PlaceSelection) => void;
  disabled?: boolean;
}

function parseAddressComponents(components: any[]): PlaceSelection {
  const get = (type: string) => {
    const c = (components || []).find((x: any) => (x.types || []).includes(type));
    const name = c?.longText || c?.shortText || c?.name || '';
    return name;
  };
  return {
    city: get('locality') || get('sublocality'),
    state: get('administrative_area_level_1'),
    country: get('country') || get('region'),
  } as PlaceSelection;
}

export default function PlaceAutocomplete({ value = '', onSelect, disabled }: PlaceAutocompleteProps) {
  const [query, setQuery] = useState<string>(value);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<AutocompleteSuggestion[]>([]);
  const [useOsm, setUseOsm] = useState<boolean>(!MAPS_API_KEY || USE_OSM_ONLY);

  const canUseApi = useMemo(() => !!MAPS_API_KEY, []);

  useEffect(() => {
    if (!query || query.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        setLoading(true);
        setError(null);
        if (canUseApi && !useOsm && !USE_OSM_ONLY) {
          const res = await fetch('https://places.googleapis.com/v1/places:autocomplete', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Goog-Api-Key': MAPS_API_KEY as string,
              'X-Goog-FieldMask': 'places.id,places.displayName,places.formattedAddress',
            },
            body: JSON.stringify({ input: query, languageCode: 'en' }),
          });
          if (!res.ok) {
            // If forbidden or any failure, switch to OSM fallback
            setUseOsm(true);
          } else {
            const data = await res.json();
            const list = (data?.places || []).map((p: any) => ({
              id: p.id,
              primaryText: p.displayName?.text || p.displayName || '',
              secondaryText: p.formattedAddress || '',
            }));
            setSuggestions(list);
          }
        }
        if (!canUseApi || useOsm || USE_OSM_ONLY) {
          const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&q=${encodeURIComponent(query)}&limit=6`;
          const osmRes = await fetch(url, {
            headers: {
              'Accept': 'application/json',
            },
          });
          const osmData = await osmRes.json();
          const list: AutocompleteSuggestion[] = (osmData || []).map((p: any) => ({
            id: String(p.place_id),
            primaryText: p.display_name || '',
            secondaryText: p.type || '',
            lat: p.lat ? Number(p.lat) : undefined,
            lon: p.lon ? Number(p.lon) : undefined,
          }));
          setSuggestions(list);
        }
      } catch (e: any) {
        setError('Failed to fetch suggestions');
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query, canUseApi, useOsm]);

  async function handleSelect(id: string) {
    try {
      setLoading(true);
      setError(null);
      if (!useOsm && canUseApi) {
        const res = await fetch(`https://places.googleapis.com/v1/places/${id}`, {
          headers: {
            'X-Goog-Api-Key': MAPS_API_KEY as string,
            'X-Goog-FieldMask': 'id,displayName,formattedAddress,location,addressComponents',
          },
        });
        if (!res.ok) {
          // Fall back if details request fails
          setUseOsm(true);
        } else {
          const p = await res.json();
          const sel: PlaceSelection = {
            name: p.displayName?.text || p.displayName || undefined,
            address: p.formattedAddress || undefined,
            coordinates: p.location ? { lat: p.location.latitude, lng: p.location.longitude } : undefined,
            ...parseAddressComponents(p.addressComponents || []),
          };
          onSelect(sel);
          setSuggestions([]);
          return;
        }
      }
      // OSM selection path: find the selected suggestion with lat/lon
      const s = suggestions.find((x) => x.id === id);
      if (s && typeof s.lat === 'number' && typeof s.lon === 'number') {
        onSelect({
          name: s.primaryText,
          address: s.primaryText,
          coordinates: { lat: s.lat, lng: s.lon },
        });
      }
      setSuggestions([]);
    } catch (e: any) {
      setError('Failed to load place');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={'Search place'}
          className="input flex-1"
          disabled={disabled}
        />
        {loading && <span className="text-xs text-theme-secondary">Loading…</span>}
      </div>
      {!!error && <div className="text-xs text-red-500">{error}</div>}
      {suggestions.length > 0 && (
        <div className="rounded-md border p-1 bg-[var(--card)]" style={{ borderColor: 'var(--border)' }}>
          {suggestions.map((s) => (
            <button
              key={s.id}
              type="button"
              className="w-full text-left px-2 py-1 rounded hover:bg-white/10"
              onClick={() => handleSelect(s.id)}
            >
              <div className="text-sm">{s.primaryText}</div>
              {s.secondaryText && <div className="text-xs text-theme-secondary">{s.secondaryText}</div>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}